// components/portal/SourceLaunchComposer.tsx
'use client';

import { formatOptions } from '#lib/leasing/formatFit';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { assetSourcingBrief, CnccMultiSelect, CountryMultiSelect, IdMultiSelect, PILL_CHIP_CLASS } from './SourceSetupPanel';
import { ArrowUp, SlidersHorizontal } from '@/components/ui/lucide-memo';
import Switch from '@/components/ui/Switch';
import RangeControl from '@/components/ui/RangeControl';
import type { SourceCountryMode } from '#lib/leasing/source-country-scope';
import VoiceInputButton from '@/components/VoiceInputButton';
import { UnitMiniPlan } from './UnitMiniPlan';
import { brandsForCredits } from '#lib/leasing/sourceBudget';
import { formatsForUnitType, shownUnitCode, surfaceBandFor } from '#lib/leasing/unitLaunch';
import { MANDATE_DEFAULT_TARGET_SCORE, MANDATE_MAX_CEILING_CREDITS, MANDATE_MIN_TARGET_SCORE } from '#lib/leasing/sourcingMandate';
import type { LeasingPropertyDTO, LeasingUnitDTO, SourcingPresetParams } from '#types/types';

/**
 * THE LAUNCH BAR at the top of the Shortlist dashboard (owner, 2026-09-28): the user says in
 * their words what they need, names the assets and a credit ceiling, presses Launch, and the agent
 * sets the campaign up and runs it under that mandate. It replaced the "Create a new campaign"
 * halo button as the primary way to start a run; the toolbar's "New campaign" keeps the form.
 *
 * Drawn as ChatGPT's composer (owner, 2026-09-28), measured off its screenshot: a #212121 card
 * with 28px corners holding only the words, the mic and a blue round arrow; the technical
 * settings (assets, unit, credit ceiling, target score) sit in a darker ribbon tucked under it.
 */
const DEFAULT_CEILING = 60;
// One font for the digits and their invisible sizer, or the width it measures is not the width drawn.
const DIGITS_STYLE: React.CSSProperties = { fontSize: 12, lineHeight: '14px', fontFamily: 'inherit', fontWeight: 500 };
const MIN_CEILING = 5;

const Field = ({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <p className="mb-1 text-[#8E8E8E]" style={{ fontSize: 12 }}>{label}</p>
    {children}
  </div>
);

// Digits only, no native spinner: the browser's arrows are not ours (owner, 2026-09-28).
const readDigits = (raw: string) => Number(raw.replace(/\D/g, '') || '0');

/** A ribbon pill holding an editable number and its unit, sized to exactly its digits. */
const DigitsPill = ({ value, onChange, unit, label, disabled }: { value: number; onChange: (next: number) => void; unit: string; label: string; disabled: boolean }) => (
  <label className={PILL_CHIP_CLASS}>
    {/* An invisible copy of the value sets the width and the input sits on top of it, so the
        pill's padding is the only space around the number. */}
    <span className="inline-grid">
      <span aria-hidden className="invisible col-start-1 row-start-1 whitespace-pre tabular-nums" style={DIGITS_STYLE}>{value}</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        // size 1: an input's own default width (about 20 characters) would otherwise set the
        // grid track, and the pill grew to 200px around "60".
        size={1}
        value={value}
        onChange={(event) => onChange(readDigits(event.target.value))}
        disabled={disabled}
        aria-label={label}
        className="col-start-1 row-start-1 w-full min-w-0 bg-transparent p-0 tabular-nums text-white focus:outline-none"
        style={DIGITS_STYLE}
      />
    </span>
    <span className="text-white/60" style={{ lineHeight: '14px' }}>{unit}</span>
  </label>
);

/**
 * `propertyId` locks the bar to one centre (Plan, which launches a run for the plan's own centre) and hides the asset
 * picker; `onLaunched` hears the new run's id as soon as it exists.
 */
export const SourceLaunchComposer = ({ propertyId, onLaunched }: { propertyId?: string; onLaunched?: (campaignId: string) => void } = {}) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((s) => s.language);
  const assets = useKernelStore((s) => s.sourceAssets);
  const propertyRegistry = useKernelStore((s) => s.inventory.Property.registry);
  const recentAssetIds = useKernelStore((s) => s.userPreferences.sourceRecentAssetIds);
  const fr = lang === 'fr';

  // The same asset list as the Assets tab, loaded once here so the bar is usable from the Shortlist tab.
  useEffect(() => {
    if (assets.status === 'idle') void kernel.current?.loadSourceAssets();
  }, [kernel, assets.status]);

  const options = useMemo(
    () =>
      assets.ids
        .map((id) => propertyRegistry[id] as LeasingPropertyDTO | undefined)
        .filter((p): p is LeasingPropertyDTO => Boolean(p))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }))
        .map((p) => ({ id: p.id, label: p.name || p.id })),
    [assets.ids, propertyRegistry],
  );

  const [prompt, setPrompt] = useState('');
  const [assetIds, setAssetIds] = useState<string[]>(propertyId ? [propertyId] : []);
  // A UNIT FIT: one unit of the one selected asset, or none for the whole asset. The chip only
  // shows with a single asset picked; its list is every unit of the asset, fetched on demand.
  const [unitId, setUnitId] = useState<string | null>(null);
  const [unitEdits, setUnitEdits] = useState<{ surfaceMinSqm?: number; surfaceMaxSqm?: number; acceptedFormats?: string[] }>({});
  const unitRegistry = useKernelStore((s) => s.inventory.Unit.registry);
  const singleAssetId = assetIds.length === 1 ? assetIds[0]! : null;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a unit belongs to one asset: changing the asset drops it
    setUnitId(null);
    setUnitEdits({});
    if (!singleAssetId) return;
    void kernel.current?.queryInventory('Unit', { filters: { propertyId: singleAssetId }, limit: 200, offset: 0 });
  }, [kernel, singleAssetId]);
  // Lease ends as Analyst reads them (property detail rentRoll, mock-ups included on a mock-up asset).
  const assetRentRoll = useKernelStore((st) => (singleAssetId ? (st.inventory.Property.registry[singleAssetId] as LeasingPropertyDTO | undefined)?.rentRoll : undefined));
  const [yearAhead] = useState(() => new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10));
  const leaseEndOf = useMemo(() => new Map((assetRentRoll ?? []).map((r) => [r.unitId, r.leaseEnd])), [assetRentRoll]);
  const unitOptions = useMemo(
    () =>
      Object.values(unitRegistry)
        // Every unit of the asset (owner, 2026-10-14: a let shop picked on the map read as a raw id when
        // the list held only the vacant ones). Whether a let unit can be sourced is the launch's call.
        .filter((u): u is LeasingUnitDTO => Boolean(u) && (u as LeasingUnitDTO).propertyId === singleAssetId)
        .sort((a, b) => a.unitIdentifier.localeCompare(b.unitIdentifier, 'fr', { numeric: true }))
        .map((u) => {
          // A lease ending within a year is what makes a let unit worth sourcing; the label says when.
          const end = leaseEndOf.get(u.id);
          const soon = end && end < yearAhead ? `${fr ? 'fin' : 'ends'} ${end.slice(0, 7)}` : null;
          return { id: u.id, label: [shownUnitCode(u.unitIdentifier), u.tenantName ?? (shownUnitCode(u.unitIdentifier) ? null : u.unitIdentifier), u.areaSqm ? `${Math.round(u.areaSqm)} m²` : null, soon].filter(Boolean).join(' · ') };
        }),
    [unitRegistry, singleAssetId, leaseEndOf, fr, yearAhead],
  );
  const [ceiling, setCeiling] = useState(DEFAULT_CEILING);
  // No cap by default (owner, 2026-10-12): the run spends what reaching the target takes; a cap is opt-in.
  const [capped, setCapped] = useState(false);
  // The champion's score the agent keeps working towards; typed freely, checked at launch.
  const [targetScore, setTargetScore] = useState(MANDATE_DEFAULT_TARGET_SCORE);
  const [launching, setLaunching] = useState(false);

  // The last asset launched on is the default, the way the form's picker floats it to the top.
  useEffect(() => {
    if (propertyId || assetIds.length > 0 || options.length === 0) return;
    const recent = (recentAssetIds ?? []).find((id) => options.some((o) => o.id === id));
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a default for an empty pick, settled once the list exists
    setAssetIds([recent ?? options[0]!.id]);
  }, [options, recentAssetIds, assetIds.length, propertyId]);

  // THE BAR UNFOLDS (owner, 2026-10-05: 'clicking on a button would make this lower bar thicker with
  // the rest of the sourcing params'). It opens on what the campaign form will open on, the first
  // asset's saved brief, and keeps only what the user moves. At launch those go to the draft through
  // sourcePendingParams, the channel the form already applies for presets and the agent.
  type Brief = ReturnType<typeof assetSourcingBrief>;
  const [expanded, setExpanded] = useState(false);
  const [settled, setSettled] = useState(false);
  const [edits, setEdits] = useState<Partial<Brief> & { countryMode?: SourceCountryMode }>({});
  // PICKING A UNIT SETTLES THE BRIEF ON IT (owner, 2026-11-03): its surface band and its format, the
  // way the Judge reads a unit run. They replace what the user had typed for those fields, are
  // dropped with the unit, and anything typed after the pick still wins.
  const pickUnit = useCallback((nextId: string | null) => {
    setUnitId(nextId);
    const unit = nextId ? (unitRegistry[nextId] as LeasingUnitDTO | undefined) : undefined;
    const band = surfaceBandFor(unit?.areaSqm ?? null);
    const next = unit
      ? {
          ...(band.minSqm !== null && band.maxSqm !== null ? { surfaceMinSqm: band.minSqm, surfaceMaxSqm: band.maxSqm } : {}),
          ...(formatsForUnitType(unit.type).length ? { acceptedFormats: formatsForUnitType(unit.type) } : {}),
        }
      : {};
    setUnitEdits(next);
    // Only the fields the unit settled drop what was typed for them.
    setEdits((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !(key in next))) as typeof current);
  }, [unitRegistry]);
  const handleUnitChange = useCallback((ids: string[]) => pickUnit(ids.filter((id) => id !== unitId)[0] ?? null), [pickUnit, unitId]);
  const seed = useMemo(() => assetSourcingBrief(propertyRegistry[assetIds[0] ?? ''] as LeasingPropertyDTO | undefined), [propertyRegistry, assetIds]);
  const brief: Brief = { ...seed, ...unitEdits, ...edits };
  const countryMode = edits.countryMode ?? (seed.countryCodes.length ? 'MANUAL' : 'ASSETS');
  const edit = <K extends keyof typeof edits>(key: K) => (value: (typeof edits)[K]) => setEdits((current) => ({ ...current, [key]: value }));
  const changedCount = new Set(Object.keys({ ...unitEdits, ...edits }).map((key) => (key === 'countryMode' ? 'countryCodes' : key))).size;
  // applyParams reads a PRESENT countryCodes key as a manual scope, even undefined, so it is dropped
  // for "the assets' countries" rather than blanked.
  const { countryCodes, ...rest } = brief;
  const changedParams: SourcingPresetParams | null = changedCount
    ? { ...rest, ...(countryMode === 'MANUAL' ? { countryCodes } : {}) }
    : null;

  // The box grows with the brief (owner, 2026-10-13: a long brief sat in two lines), up to a third of
  // the window, then scrolls.
  const promptRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const box = promptRef.current;
    if (!box) return;
    box.style.height = 'auto';
    box.style.height = `${Math.min(box.scrollHeight, Math.round(window.innerHeight / 3))}px`;
  }, [prompt]);

  const canLaunch = !launching && prompt.trim().length > 0 && assetIds.length > 0 && (!capped || ceiling >= MIN_CEILING) && targetScore >= MANDATE_MIN_TARGET_SCORE && targetScore <= 100;
  // The number the launch itself buys: brandsForCredits with research on, on each asset's even
  // share of the ceiling (the route splits a budget across assets the same way), never an estimate.
  const assetCount = Math.max(1, assetIds.length);
  const brands = brandsForCredits(ceiling / assetCount, true) * assetCount;

  const handleLaunch = useCallback(async () => {
    if (!canLaunch) return;
    setLaunching(true);
    try {
      await kernel.current?.launchSourcingFromBrief({ prompt, propertyIds: assetIds, unitId, creditCeiling: capped ? ceiling : null, targetScore, params: changedParams, onLaunched });
    } finally {
      setLaunching(false);
    }
  }, [kernel, canLaunch, prompt, assetIds, unitId, capped, ceiling, targetScore, changedParams, onLaunched]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleLaunch();
    }
  }, [handleLaunch]);

  // Dictation, the chat composer's own push-to-talk mic: final phrases append to the brief.
  const [voiceLanguage, setVoiceLanguage] = useState(fr ? 'fr-FR' : 'en-US');
  const handleTranscript = useCallback((transcript: string, isFinal: boolean) => {
    const words = transcript.trim();
    if (isFinal && words) setPrompt((prev) => (prev.trim() ? `${prev.trimEnd()} ${words}` : words));
  }, []);

  const handleCeilingChange = useCallback((next: number) => setCeiling(Math.min(MANDATE_MAX_CEILING_CREDITS, next)), []);
  const handleTargetChange = useCallback((next: number) => setTargetScore(Math.min(100, next)), []);

  return (
    <div className="w-full max-w-3xl text-white" data-testid="source-launch-composer">
      <div className="relative z-10 rounded-[28px] border border-[#262626] bg-[#1B1B1B] px-4 pb-3 pt-3">
        <textarea
          ref={promptRef}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          placeholder={fr ? 'Décrivez le sourcing dont vous avez besoin' : 'Describe the sourcing you need'}
          className="custom-scrollbar w-full resize-none bg-transparent px-1 py-1 text-white placeholder:text-[#8E8E8E] focus:outline-none"
          style={{ fontSize: 16 }}
        />
        <div className="flex items-center justify-end gap-2">
          <VoiceInputButton
            onTranscriptUpdate={handleTranscript}
            disabled={launching}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08] disabled:opacity-50"
            iconSize={20}
            iconStrokeWidth={2}
            iconColor="currentColor"
            currentLanguage={voiceLanguage}
            onSetLanguage={setVoiceLanguage}
          />
          <button
            type="button"
            onClick={handleLaunch}
            disabled={!canLaunch}
            aria-label={fr ? 'Lancer' : 'Launch'}
            title={fr ? 'Lancer' : 'Launch'}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#3862C2] text-white transition-colors hover:bg-[#4270D6] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {launching
              ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              : <ArrowUp size={19} strokeWidth={2.6} />}
          </button>
        </div>
      </div>
      {/* The ribbon: settings, not words, so they live apart from the brief. */}
      <div className="mx-5 rounded-b-[16px] bg-[#141414] px-3 py-1.5 text-[#CDCDCD]">
      <div className="flex flex-wrap items-center gap-2">
        {propertyId ? null : <IdMultiSelect
          options={options}
          value={assetIds}
          onChange={setAssetIds}
          emptyLabel={fr ? 'Choisir un actif' : 'Choose an asset'}
          disabled={launching}
          direction="down"
          variant="pill"
        />}
        {singleAssetId ? (
          <IdMultiSelect
            options={unitOptions}
            value={unitId ? [unitId] : []}
            onChange={handleUnitChange}
            emptyLabel={fr ? 'Tout l’actif' : 'Whole asset'}
            disabled={launching}
            direction="down"
            variant="pill"
          />
        ) : null}
        <Switch checked={capped} onCheckedChange={setCapped} labelRight={fr ? 'Plafond' : 'Credit cap'} ariaLabel={fr ? 'Plafonner les crédits' : 'Cap the credits'} disabled={launching} />
        {capped ? <DigitsPill value={ceiling} onChange={handleCeilingChange} unit={fr ? 'crédits' : 'credits'} label={fr ? 'Plafond de crédits' : 'Credit ceiling'} disabled={launching} /> : null}
        <DigitsPill value={targetScore} onChange={handleTargetChange} unit={fr ? 'visé' : 'target'} label={fr ? 'Score visé pour le champion' : 'Target score for the champion'} disabled={launching} />
        <span className="min-w-0 flex-1 truncate text-[#6E6E6E]" style={{ fontSize: 11 }}>
          {capped
            ? (fr ? `${brands} enseignes analysées au plus` : `${brands} brands analysed at most`)
            : (fr ? `Jusqu’à une enseigne à ${targetScore}` : `Until a brand reaches ${targetScore}`)}
        </span>
        <button
          type="button"
          onClick={() => {
            setSettled(!expanded && matchMedia('(prefers-reduced-motion: reduce)').matches);
            setExpanded(!expanded);
          }}
          aria-expanded={expanded}
          title={expanded ? (fr ? 'Moins de paramètres' : 'Fewer parameters') : (fr ? 'Plus de paramètres' : 'More parameters')}
          aria-label={fr ? 'Paramètres de la campagne' : 'Campaign parameters'}
          className={`inline-flex h-8 min-w-8 shrink-0 items-center justify-center gap-1 rounded-full px-2 transition-colors ${expanded ? 'bg-white/[0.12] text-white' : changedCount ? 'bg-white/[0.08] text-white' : 'text-[#AFAFAF] hover:bg-white/[0.06] hover:text-white'}`}
          style={{ fontSize: 12 }}
        >
          <SlidersHorizontal size={15} />
          {changedCount ? <span style={{ fontSize: 'inherit' }}>{changedCount}</span> : null}
        </button>
      </div>
      {/* The bar thickens rather than pops: a grid row eased from 0fr to 1fr. Clipped while it moves,
          then unclipped once settled, so the pickers' menus can drop past the bar. The transition is
          inline because globals.css gives every div `transition: scrollbar-color`, which beats a
          utility class. */}
      <div
        className={`grid motion-reduce:transition-none! ${expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        style={{ transition: 'grid-template-rows 220ms ease-out, opacity 220ms ease-out' }}
        onTransitionEnd={(event) => { if (event.target === event.currentTarget && expanded) setSettled(true); }}
        inert={!expanded}
        data-source-params
      >
        <div className={`min-h-0 ${settled ? 'overflow-visible' : 'overflow-hidden'}`}>
          <div className="grid grid-cols-1 gap-x-5 gap-y-3 px-1 pb-2 pt-3 sm:grid-cols-2">
            <Field label={fr ? 'Pays' : 'Countries'} wide>
              <CountryMultiSelect mode={countryMode} value={brief.countryCodes} onChange={edit('countryCodes')} onModeChange={edit('countryMode')} effectiveCountryCodes={brief.countryCodes} lang={lang} />
            </Field>
            <Field label={fr ? 'Catégories CNCC' : 'CNCC categories'} wide>
              <CnccMultiSelect value={brief.acceptedCnccActivities} onChange={edit('acceptedCnccActivities')} lang={lang} />
            </Field>
            <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:col-span-2 sm:grid-cols-3">
              <Field label={fr ? 'Surface min. (m²)' : 'Min. surface (m²)'}>
                <RangeControl hideLabel min={0} max={5000} step={10} value={brief.surfaceMinSqm} onValueChange={edit('surfaceMinSqm')} />
              </Field>
              <Field label={fr ? 'Surface max. (m²)' : 'Max. surface (m²)'}>
                <RangeControl hideLabel min={0} max={10000} step={10} value={brief.surfaceMaxSqm} onValueChange={edit('surfaceMaxSqm')} />
              </Field>
              <Field label={fr ? 'Format' : 'Format'}>
                <IdMultiSelect options={formatOptions(lang)} value={brief.acceptedFormats} onChange={edit('acceptedFormats')} emptyLabel={fr ? 'Tous les formats' : 'Any format'} />
              </Field>
              <Field label={fr ? 'Magasins min.' : 'Min. stores'}>
                <RangeControl hideLabel min={1} max={500} step={1} value={brief.minimumNetworkSize} onValueChange={edit('minimumNetworkSize')} />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1 sm:col-span-2">
              {([
                [fr ? 'Exclure les récemment évalués' : 'Exclude recently scored', brief.excludeRecentlyScored, edit('excludeRecentlyScored')],
                [fr ? 'Exclure les enseignes présentes' : 'Exclude on-site tenants', brief.excludeOnsiteTenants, edit('excludeOnsiteTenants')],
                [fr ? 'Site web connu' : 'Known website only', brief.requireKnownWebsite, edit('requireKnownWebsite')],
                [fr ? 'Masquer les hors cible' : 'Hide off-scope brands', brief.excludeOffScope, edit('excludeOffScope')],
              ] as const).map(([label, checked, set]) => (
                <Switch key={label} checked={checked} onCheckedChange={set} labelRight={label} ariaLabel={label} disabled={launching} />
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* The asset at a glance, under the parameters and folded with them (owner, 2026-10-13): its plan
          and rent roll, the picked unit and who trades around it. */}
      <div className={`grid motion-reduce:transition-none! ${singleAssetId && expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} style={{ transition: 'grid-template-rows 220ms ease-out, opacity 220ms ease-out' }}>
        <div className="min-h-0 overflow-hidden">{singleAssetId && expanded ? <UnitMiniPlan propertyId={singleAssetId} unitId={unitId} onSelectUnit={pickUnit} lang={fr ? 'fr' : 'en'} /> : null}</div>
      </div>
      </div>
    </div>
  );
};

SourceLaunchComposer.displayName = 'SourceLaunchComposer';
